import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from './Button';

export interface ErrorStateProps {
  title?: string;
  /**
   * Heading level of the title. `h1` when the error replaces the whole page
   * (a detail page whose load failed), so the page still has one H1.
   */
  titleAs?: 'h1' | 'h2' | 'h3';
  message?: string;
  onRetry?: () => void;
  onBack?: () => void;
  backLabel?: string;
  /** The Retry button's text (a W3 hub passes its catalogue's). */
  retryLabel?: string;
  className?: string;
}

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

/** Centered error placeholder with optional Retry / Back actions. */
export function ErrorState({
  title = 'Oops!',
  titleAs: Title = 'h3',
  message = 'Something went wrong',
  onRetry,
  onBack,
  backLabel = 'Go back',
  retryLabel = 'Retry',
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center px-6 py-16 text-center',
        className,
      )}
    >
      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-error/10">
        <AlertCircle size={40} className="text-error" aria-hidden="true" />
      </div>
      <Title className="mt-5 text-lg font-bold text-ink">{title}</Title>
      <p className="mt-2 max-w-sm text-center text-sm text-ink-secondary">
        {message}
      </p>
      {(onRetry || onBack) && (
        <div className="mt-6 flex flex-col items-center gap-2">
          {onRetry && (
            <Button
              variant="primary"
              onClick={onRetry}
              leftIcon={<RefreshCw size={16} aria-hidden="true" />}
            >
              {retryLabel}
            </Button>
          )}
          {onBack && (
            <Button
              variant="ghost"
              onClick={onBack}
              leftIcon={<ArrowLeft size={16} aria-hidden="true" />}
            >
              {backLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
